import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import Search from '../components/Search';
import { Check, X, UserCheck, Clock } from 'lucide-react';
import type { Friend, Profile } from '../types/database';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import FriendSuggestions from '../components/FriendSuggestions';

interface FriendRequest extends Friend {
  sender: Profile;
  receiver: Profile;
}

export default function Friends() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [receivedRequests, setReceivedRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [friends, setFriends] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (user) {
      loadFriendRequests();
      
      // Set up subscription for real-time updates
      const friendsChannel = supabase
        .channel('friends-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'friends'
          },
          (payload) => {
            loadFriendRequests();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(friendsChannel);
      };
    }
  }, [user]);

  const loadFriendRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get received requests
      const { data: received, error: receivedError } = await supabase
        .from('friends')
        .select(`
          *,
          sender:profiles!friends_sender_id_fkey(id, full_name, avatar_url),
          receiver:profiles!friends_receiver_id_fkey(id, full_name, avatar_url)
        `)
        .eq('receiver_id', user.id)
        .eq('status', 'pending');

      if (receivedError) throw receivedError;

      // Get sent requests
      const { data: sent, error: sentError } = await supabase
        .from('friends')
        .select(`
          *,
          sender:profiles!friends_sender_id_fkey(id, full_name, avatar_url),
          receiver:profiles!friends_receiver_id_fkey(id, full_name, avatar_url)
        `)
        .eq('sender_id', user.id)
        .eq('status', 'pending');

      if (sentError) throw sentError;

      // Get accepted friends
      const { data: acceptedFriends, error: friendsError } = await supabase
        .from('friends')
        .select(`
          *,
          sender:profiles!friends_sender_id_fkey(id, full_name, avatar_url),
          receiver:profiles!friends_receiver_id_fkey(id, full_name, avatar_url)
        `)
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

      if (friendsError) throw friendsError;

      setReceivedRequests(received || []);
      setSentRequests(sent || []);
      setFriends(acceptedFriends || []);
    } catch (err: any) {
      console.error('Error loading friend requests:', err);
      setError(err.message || 'Failed to load friend requests');
    } finally {
      setLoading(false);
    }
  };

  const handleFriendAction = async (requestId: string, action: 'accepted' | 'rejected') => {
    if (!user) return;

    try {
      setActionLoading(prev => ({ ...prev, [requestId]: true }));
      setError(null);

      const { error: updateError } = await supabase
        .from('friends')
        .update({ status: action })
        .eq('id', requestId)
        .eq('receiver_id', user.id);

      if (updateError) throw updateError;

      // Update local state immediately
      if (action === 'accepted') {
        const acceptedRequest = receivedRequests.find(req => req.id === requestId);
        if (acceptedRequest) {
          setFriends(prev => [...prev, {...acceptedRequest, status: 'accepted'}]);
          setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
        }
      } else {
        setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
      }
    } catch (err: any) {
      console.error('Error updating friend request:', err);
      setError(err.message || 'Failed to update friend request');
      // Refresh the lists in case of error
      await loadFriendRequests();
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleUnfriend = async (friendId: string) => {
    if (!user) return;

    try {
      setActionLoading(prev => ({ ...prev, [friendId]: true }));
      setError(null);

      const { error: deleteError } = await supabase
        .from('friends')
        .delete()
        .eq('id', friendId);

      if (deleteError) throw deleteError;

      // Update local state immediately
      const isSentRequest = sentRequests.some(req => req.id === friendId);
      const isAcceptedFriend = friends.some(friend => friend.id === friendId);
      
      if (isSentRequest) {
        setSentRequests(prev => prev.filter(req => req.id !== friendId));
      } else if (isAcceptedFriend) {
        setFriends(prev => prev.filter(friend => friend.id !== friendId));
      }
    } catch (err: any) {
      console.error('Error unfriending user:', err);
      setError(err.message || 'Failed to unfriend user');
      // Refresh the lists in case of error
      await loadFriendRequests();
    } finally {
      setActionLoading(prev => ({ ...prev, [friendId]: false }));
    }
  };

  const getAvatarUrl = (profile?: Profile | null) => {
    if (!profile) return `https://api.dicebear.com/7.x/initials/svg?seed=anonymous`;

    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-8">
      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">Find Friends</h2>
        <div className="max-w-xl mx-auto">
          <Search fullWidth onFriendRequestSent={loadFriendRequests} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Friend Requests Section */}
      {receivedRequests.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Friend Requests</h2>
          <div className="space-y-4">
            {receivedRequests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div 
                  className="flex items-center space-x-4 cursor-pointer"
                  onClick={() => request.sender?.id && navigate(`/user/${request.sender.id}`)}
                >
                  <img
                    src={getAvatarUrl(request.sender)}
                    alt={request.sender?.full_name || 'User'}
                    className="h-12 w-12 rounded-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${request.sender?.full_name || 'anonymous'}`;
                    }}
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      {request.sender?.full_name}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleFriendAction(request.id, 'accepted')}
                    disabled={actionLoading[request.id]}
                    className={cn(
                      "p-2 rounded-full text-green-600 hover:bg-green-50 transition-colors",
                      actionLoading[request.id] && "opacity-50 cursor-not-allowed"
                    )}
                    title="Accept"
                  >
                    <Check className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleFriendAction(request.id, 'rejected')}
                    disabled={actionLoading[request.id]}
                    className={cn(
                      "p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors",
                      actionLoading[request.id] && "opacity-50 cursor-not-allowed"
                    )}
                    title="Reject"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Requests Section */}
      {sentRequests.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Sent Requests</h2>
          <div className="space-y-4">
            {sentRequests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div 
                  className="flex items-center space-x-4 cursor-pointer"
                  onClick={() => request.receiver?.id && navigate(`/user/${request.receiver.id}`)}
                >
                  <img
                    src={getAvatarUrl(request.receiver)}
                    alt={request.receiver?.full_name || 'User'}
                    className="h-12 w-12 rounded-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${request.receiver?.full_name || 'anonymous'}`;
                    }}
                  />
                  <div>
                    <p className="font-medium text-gray-900">
                      {request.receiver?.full_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleUnfriend(request.id)}
                    disabled={actionLoading[request.id]}
                    className={cn(
                      "p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors",
                      actionLoading[request.id] && "opacity-50 cursor-not-allowed"
                    )}
                    title="Cancel Request"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <div className="text-blue-500">
                    <Clock className="h-5 w-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friend Suggestions Section */}
      <FriendSuggestions onFriendRequestSent={loadFriendRequests} />

      {/* Friends List Section */}
      {friends.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Friends</h2>
          <div className="space-y-4">
            {friends.map((friend) => {
              const friendProfile = friend.sender_id === user?.id ? friend.receiver : friend.sender;
              return (
                <div
                  key={friend.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div 
                    className="flex items-center space-x-4 cursor-pointer"
                    onClick={() => friendProfile?.id && navigate(`/user/${friendProfile.id}`)}
                  >
                    <img
                      src={getAvatarUrl(friendProfile)}
                      alt={friendProfile?.full_name || 'User'}
                      className="h-12 w-12 rounded-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${friendProfile?.full_name || 'anonymous'}`;
                      }}
                    />
                    <div>
                      <p className="font-medium text-gray-900">
                        {friendProfile?.full_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleUnfriend(friend.id)}
                      disabled={actionLoading[friend.id]}
                      className={cn(
                        "p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors",
                        actionLoading[friend.id] && "opacity-50 cursor-not-allowed"
                      )}
                      title="Unfriend"
                    >
                      <X className="h-5 w-5" />
                    </button>
                    <div className="text-green-500">
                      <UserCheck className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && receivedRequests.length === 0 && sentRequests.length === 0 && friends.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500">
            No friend requests or connections yet. Use the search above to find friends!
          </p>
        </div>
      )}
    </div>
  );
}